import * as yup from "yup";
import { useFormik } from "formik";
import { useApiCall } from "../../../../component/custom-hooks/api-call";
import {
    FormArea,
    FeildWraper,
    SelectSearch,
    FeildError,
    FormLabel,
    FileUploadProgressBar,
} from "../../../../component/form/form-layout";
import {
    Table,
    TableBody,
    TableCard,
    TableCell,
    TableHead,
    TableRow,
} from "../../../../component/table/table-layout";
import { Copy, Minus, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import moment from "moment";

const AddPopup = ({
    onClose = () => { },
    onError = () => { },
    onSubmit = () => { },
    currentData,
    dropdowns,
    Style,
}) => {
    const apiCall = useApiCall();
    const [loadingItems, setLoadingItems] = useState(false);

    /* ===================== VALIDATION ===================== */
    const validationSchema = yup.object({
        invoice_month: yup.string().required('This Field is required'),
        invoice_date: yup.string().required("Required"),
        bill_receipt_number: yup.string().required("Required"),
        vendor_id: yup.number().required("Required"),
        location_id: yup.number().required("Required"),
        total_amount: yup.number().positive().required("Required"),
        invoice_path: yup.string().required("Required"),
        invoice_status: yup.string().required("Required"),

        purchases: yup
            .array()
            .of(
                yup.object({
                    item_id: yup.number().required("Item required"),
                    variant_id: yup.number().nullable(),
                    quantity: yup
                        .number()
                        .required("Qty required")
                        .positive("Qty > 0")
                        .integer("Must be integer"),
                    amount: yup
                        .number()
                        .required("Amount required")
                        .positive("Amount > 0"),
                    note: yup.string().nullable(),
                })
            )
            .min(1, "At least one item is required"),
    });

    /* ===================== FORMIK ===================== */
    const formik = useFormik({
        initialValues: {
            invoice_month: "",
            invoice_date: "",
            bill_receipt_number: "",
            vendor_id: "",
            total_amount: "",
            location_id: "",
            notes: "",
            invoice_path: "",
            invoice_status: "",
            notes: "",
            purchases: [
                { item_id: "", variant_id: "", quantity: 1, amount: "", note: "" },
            ],
        },
        validationSchema,
        validateOnChange: false,
        validateOnBlur: false,
        onSubmit: async (values) => {
            const itemsTotal = values.purchases.reduce(
                (sum, i) => sum + Number(i.amount || 0),
                0
            );

            if (Number(values.total_amount).toFixed(2) !== itemsTotal.toFixed(2)) {
                onError("Total Amount must equal sum of item amounts");
                return;
            }

            const payload = {
                ...(currentData?.id && {
                    purchase_id: currentData.id,
                    invoice_id: currentData.invoice_id,
                }),
                ...values,
                purchases: values.purchases.map(({ amount, note, ...rest }) => ({
                    ...rest,
                    total_amount: Number(amount),
                    notes: note,
                })),
            };

            try {
                const res = await apiCall({
                    method: "post",
                    url: currentData?.id ? "update-purchase" : "store-purchase",
                    data: JSON.stringify(payload),
                });
                onSubmit(res?.message || "Success");
                formik.resetForm();
            } catch (err) {
                onError(
                    err?.response?.data?.message || "Something went wrong"
                );
            }
        },
    });

    console.log("currentData123", currentData);

    /* ===================== EDIT MODE ===================== */
    useEffect(() => {
        if (!currentData?.id) return;

        const loadEditData = async () => {
            setLoadingItems(true);
            try {
                const res = await apiCall({
                    method: "get",
                    url: `get-purchase-for-edit?purchase_id=${currentData.id}`,
                });

                const items = res?.data?.purchases || [];

                formik.setValues({
                    invoice_month: currentData?.invoice_month ? moment(currentData?.invoice_month, "MM-YYYY").format("YYYY-MM") : "",
                    invoice_date: currentData?.date
                        ? moment(currentData.date).format("YYYY-MM-DD")
                        : "",
                    bill_receipt_number: currentData?.bill_receipt_number ?? "",
                    vendor_id: currentData?.vendor_id ?? "",
                    total_amount: currentData?.total_amount?.replaceAll("PKR ", "")?.replaceAll(",", "") ?? "",
                    location_id: currentData?.location_id ?? "",
                    notes: currentData?.notes ?? "",
                    invoice_status: currentData?.invoice_status ?? "",
                    invoice_path: currentData?.invoice_path ?? "",
                    purchases: items.map(({ total_amount, notes, ...rest }) => ({
                        ...rest,
                        amount: Number(total_amount),
                        note: notes,
                    })),
                });
            } finally {
                setLoadingItems(false);
            }
        };

        loadEditData();
    }, [currentData]);

    /* ===================== ROW HANDLERS ===================== */
    const addRow = () =>
        formik.setFieldValue("purchases", [
            ...formik.values.purchases,
            { item_id: "", variant_id: "", quantity: 1, amount: "", note: "" },
        ]);

    const removeRow = (idx) => {
        const rows = [...formik.values.purchases];
        rows.splice(idx, 1);
        formik.setFieldValue("purchases", rows);
    };

    const copyRow = (idx) =>
        formik.setFieldValue("purchases", [
            ...formik.values.purchases,
            formik.values.purchases[idx],
        ]);


    /* ===================== UI ===================== */
    return (
        <form onSubmit={formik.handleSubmit} className={Style.modal_main}>
            <div className={Style.modal_body}>
                <FormArea>

                    {/* BASIC FIELDS */}
                    {[
                        ["Invoice Month", "invoice_month", "month"],
                        ["Invoice Date", "invoice_date", "date"],
                        ["Bill Receipt Number", "bill_receipt_number", "text"],
                        ["Total Amount", "total_amount", "number"],
                    ].map(([label, name, type]) => (
                        <FeildWraper key={name}>
                            <FormLabel required>{label}</FormLabel>
                            <input
                                type={type}
                                name={name}
                                value={formik.values[name]}
                                onChange={formik.handleChange}
                            />
                            {formik.touched[name] && formik.errors[name] && (
                                <FeildError>{formik.errors[name]}</FeildError>
                            )}
                        </FeildWraper>
                    ))}

                    {/* SELECTS */}
                    <FeildWraper>
                        <FormLabel required>Vendor</FormLabel>
                        <SelectSearch
                            value={formik.values.vendor_id}
                            onChange={(v) => formik.setFieldValue("vendor_id", v)}
                            options={dropdowns?.vendor?.map(v => ({
                                label: v.full_name,
                                value: v.id,
                            }))}
                        />
                        <FeildError>{formik.errors.vendor_id}</FeildError>
                    </FeildWraper>

                    <FeildWraper>
                        <FormLabel required>Building</FormLabel>
                        <SelectSearch
                            value={formik.values.location_id}
                            onChange={(v) => formik.setFieldValue("location_id", v)}
                            options={dropdowns?.buildings?.map(b => ({
                                label: b.name,
                                value: b.id,
                            }))}
                        />
                        <FeildError>{formik.errors.location_id}</FeildError>
                    </FeildWraper>

                    <FeildWraper>
                        <FormLabel required>Invoice Status</FormLabel>
                        <SelectSearch
                            value={formik.values.invoice_status}
                            onChange={(v) => formik.setFieldValue("invoice_status", v)}
                            options={[{label: "Paid", value: "paid"}, {label: "Unpaid", value: "unpaid"}]}
                        />
                        <FeildError>{formik.errors.invoice_status}</FeildError>
                    </FeildWraper>

                    <FeildWraper>
                        <FormLabel required>Notes</FormLabel>
                        <textarea
                            name={"notes"}
                            value={formik.values?.notes}
                            onChange={formik.handleChange}
                        />
                        {formik.touched?.notes && formik.errors?.notes && (
                            <FeildError>{formik.errors?.notes}</FeildError>
                        )}
                    </FeildWraper>

                    {/* FILE */}
                    <FeildWraper>
                        <FormLabel required>Invoice</FormLabel>
                        <FileUploadProgressBar
                            value={formik.values.invoice_path}
                            onChange={(f) => formik.setFieldValue("invoice_path", f)}
                            initialFiles={currentData?.invoice_path ? [{
                                name: currentData?.invoice_path?.split("/")[currentData?.invoice_path?.split("/")?.length - 1]
                            }] : []}
                        />
                        <FeildError>{formik.errors.invoice_path}</FeildError>
                    </FeildWraper>

                    {/* TABLE */}
                    <FeildWraper width="100%">
                        {loadingItems && <p>Loading items…</p>}
                        <TableCard>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell header sticky />
                                        <TableCell header>Item</TableCell>
                                        <TableCell header>Variant</TableCell>
                                        <TableCell header>Qty</TableCell>
                                        <TableCell header>Amount</TableCell>
                                        <TableCell header>Note</TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {formik.values.purchases.map((row, idx) => (
                                        <TableRow key={idx}>
                                            <TableCell sticky className={'!w-fit min-w-0 !px-1'}>
                                                <div className="flex items-center gap-0.5">
                                                    {/* Add Row */}
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            addRow();
                                                        }}
                                                        className="flex h-4 w-4 items-center justify-center rounded-sm border border-gray-300 bg-white text-gray-600hover:bg-green-50 hover:text-green-600 hover:border-green-400 transition-all duration-200"
                                                    >
                                                        <Plus size={10} />
                                                    </button>

                                                    <button
                                                        type="button"
                                                        onClick={() => copyRow(idx)}
                                                        className="flex h-4 w-4 items-center justify-center rounded-sm border border-gray-300 bg-white text-gray-600hover:bg-green-50 hover:text-green-600 hover:border-green-400 transition-all duration-200"
                                                    >
                                                        <Copy size={8} />
                                                    </button>

                                                    {/* Remove Row */}
                                                    {formik.values.purchases.length > 1 && (
                                                        <button
                                                            type="button"
                                                            onClick={() => removeRow(idx)}
                                                            className="flex h-4 w-4 items-center justify-center rounded-sm border border-gray-300 bg-white text-gray-600 hover:bg-red-50 hover:text-red-600 hover:border-red-400 transition-all duration-200"
                                                        >
                                                            <Minus size={10} />
                                                        </button>
                                                    )}
                                                </div>
                                            </TableCell>

                                            <TableCell>
                                                <SelectSearch
                                                    menuPosition="fixed"
                                                    className={'!w-[180px]'}
                                                    value={row.item_id}
                                                    onChange={(v) => formik.setFieldValue(`purchases.${idx}.item_id`, v)}
                                                    options={dropdowns?.items?.map(i => ({
                                                        label: i.name,
                                                        value: i.id,
                                                    }))}
                                                />
                                                <FeildError style={{position: "relative"}}>{formik.errors.purchases?.[idx]?.item_id}</FeildError>
                                            </TableCell>

                                            <TableCell>
                                                <SelectSearch
                                                    menuPosition="fixed"
                                                    className={'!w-[180px]'}
                                                    value={row.variant_id}
                                                    onChange={(v) => formik.setFieldValue(`purchases.${idx}.variant_id`, v)}
                                                    options={dropdowns?.variants?.map(v => ({
                                                        label: v.name,
                                                        value: v.id,
                                                    }))}
                                                />
                                            </TableCell>

                                            <TableCell>
                                                <input
                                                    className={'!w-[120px]'}
                                                    type="number"
                                                    value={row.quantity}
                                                    onChange={(e) =>
                                                        formik.setFieldValue(`purchases.${idx}.quantity`, e.target.value)
                                                    }
                                                />
                                                <FeildError style={{position: "relative"}}>{formik.errors.purchases?.[idx]?.quantity}</FeildError>
                                            </TableCell>

                                            <TableCell>
                                                <input
                                                    className={'!w-[120px]'}
                                                    type="number"
                                                    value={row.amount}
                                                    onChange={(e) =>
                                                        formik.setFieldValue(`purchases.${idx}.amount`, e.target.value)
                                                    }
                                                />
                                                <FeildError style={{position: "relative"}}>{formik.errors.purchases?.[idx]?.amount}</FeildError>
                                            </TableCell>

                                            <TableCell>
                                                <input
                                                    className={'!w-[180px]'}
                                                    value={row.note}
                                                    onChange={(e) =>
                                                        formik.setFieldValue(`purchases.${idx}.note`, e.target.value)
                                                    }
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableCard>
                    </FeildWraper>

                </FormArea>
            </div>

            <div className={Style.modal_foot}>
                <button type='reset' className={`${Style.dcmb__cancel}`} onClick={() => onClose()}>Discard</button>
                <button type='submit' className={`${Style.dcmb__submit}`}>Apply</button>
            </div>
        </form>
    );
};

export default AddPopup;


// import * as yup from 'yup'
// import { useFormik } from 'formik'
// import { useApiCall } from '../../../../../component/custom-hooks/api-call'
// import "react-datepicker/dist/react-datepicker.css";
// import { FormArea, FeildWraper, SelectSearch, FeildError, FormLabel, FileUploadProgressBar } from '../../../../../component/form/form-layout'
// import { Table, TableBody, TableCard, TableCell, TableHead, TableRow } from '../../../../../component/table/table-layout';
// import { Minus, Plus } from 'lucide-react';
// import { useEffect, useState } from 'react';
// import moment from 'moment';

// const AddPopup = ({ onClose = () => { }, onError = () => { }, onSubmit = () => { }, currentData, dropdowns, Style }) => {
//     const apiCall = useApiCall();
//     const [purchaseItems, setPerchaseItems] = useState([]);
//     const [purchaseItemsLoader, setPerchaseItemsLoader] = useState(false);

//     const getPerchaseItems = async () => {
//         setPerchaseItemsLoader(true);
//         try {
//             const res = await apiCall({
//                 method: "get",
//                 url: `get-purchase-for-edit?purchase_id=${currentData?.id}`
//             });

//             const data = res?.data?.purchases || [];

//             setPerchaseItems(data);

//             formik.setFieldValue(
//                 "purchases",
//                 data.map(({ total_amount, ...rest }) => ({
//                     ...rest,
//                     amount: Number(total_amount),
//                 }))
//             );
//         } catch (error) {
//             console.warn("Error : ", error);
//         } finally {
//             setPerchaseItemsLoader(false);
//         }
//     };

//     useEffect(() => {
//         if (!currentData?.id) return;

//         getPerchaseItems();

//         formik.setValues({
//             invoice_date: currentData?.date
//                 ? moment(currentData.date).format("YYYY-MM-DD")
//                 : "",
//             bill_receipt_number: currentData?.bill_receipt_number ?? "",
//             vendor_id: currentData?.vendor_id ?? "",
//             total_amount: currentData?.total_amount ?? "",
//             location_id: currentData?.location_id ?? "",
//             notes: currentData?.notes ?? "",
//             invoice_path: currentData?.invoice_path ?? "",
//         });
//     }, [currentData]);

//     const validationSchema = yup.object().shape({
//         invoice_date: yup.string().required('This Field is required'),
//         bill_receipt_number: yup.string().required('This Field is required'),
//         vendor_id: yup.number().required('This Field is required'),
//         location_id: yup.number().required('This Field is required'),
//         total_amount: yup
//             .number()
//             .required('This Field is required')
//             .positive(),
//         invoice_path: yup.string().required('This Field is required'),

//         purchases: yup
//             .array()
//             .of(
//                 yup.object().shape({
//                     item_id: yup.string().required('Item required'),
//                     variant_id: yup.string().nullable(),
//                     quantity: yup.number().positive().integer(),
//                     amount: yup.number().required('Amount required').positive(),
//                     note: yup.string().nullable(),
//                 })
//             )
//             .min(1, 'At least one item is required')
//     });

//     const formik = useFormik({
//         initialValues: {
//             invoice_date: "",
//             bill_receipt_number: "",
//             vendor_id: "",
//             total_amount: "",
//             location_id: "",
//             notes: "",
//             invoice_path: "",
//             purchases: [
//                 { item_id: "", variant_id: "", quantity: 0, amount: "", note: "" }
//             ],
//         },
//         enableReinitialize: true,
//         validationSchema,
//         onSubmit: async (values) => {
//             let extraData = {}
//             if (currentData?.id) {
//                 extraData = {
//                     "purchase_id": currentData?.id,
//                     "invoice_id": currentData?.invoice_id
//                 }
//             }
//             const apiData = JSON.stringify({
//                 ...extraData,
//                 "invoice_date": values?.invoice_date,
//                 "bill_receipt_number": values?.bill_receipt_number,
//                 "vendor_id": values?.vendor_id,
//                 "total_amount": values?.total_amount,
//                 "location_id": values?.location_id,
//                 "notes": values?.notes,
//                 "invoice_path": values?.invoice_path,
//                 purchases: values.purchases.map(({ amount, ...rest }) => ({
//                     ...rest,
//                     total_amount: Number(amount),
//                 })),

//             });

//             const itemsTotal = values.purchases.reduce((sum, item) => sum + Number(item.amount || 0), 0);
//             const roundedTotal = Number(values.total_amount).toFixed(2);
//             const roundedItems = itemsTotal.toFixed(2);

//             if (roundedTotal !== roundedItems) {
//                 onError("Total Amount must match sum of all item amounts");
//                 return;
//             }

//             try {
//                 const resp = await apiCall({ method: "post", url: currentData?.id ? `update-purchase` : `store-purchase`, data: apiData });
//                 onSubmit(resp?.message || `Request Submitted`);
//                 formik.resetForm();
//             } catch (error) {
//                 onError(error?.response.data?.respMsg || error?.response.data?.responseMessage || error?.response.data?.message || "Some Thing Went Wrong")
//             }
//         }
//     });

//     const addRow = () => {
//         formik.setFieldValue("purchases", [
//             ...formik.values.purchases,
//             { item_id: "", variant_id: "", quantity: 1, amount: "", note: "" }
//         ]);
//     };

//     const removeRow = (index) => {
//         const rows = [...formik.values.purchases];
//         rows.splice(index, 1);
//         formik.setFieldValue("purchases", rows);
//     };



//     return (
//         <>
//             <form onSubmit={formik.handleSubmit} className={Style.modal_main}>
//                 <div className={Style.modal_body}>
//                     <FormArea>
//                         <FeildWraper>
//                             <FormLabel required>Invoice Date</FormLabel>
//                             <input
//                                 type={'date'}
//                                 name={'invoice_date'}
//                                 onChange={formik.handleChange}
//                                 value={formik.values.invoice_date}
//                                 placeholder='Asset Name'
//                             />
//                             {formik.touched.invoice_date && formik.errors.invoice_date && (
//                                 <FeildError>{formik.errors.invoice_date}</FeildError>
//                             )}
//                         </FeildWraper>

//                         <FeildWraper>
//                             <FormLabel required>Bill Receipt Number</FormLabel>
//                             <input
//                                 type={'text'}
//                                 name={'bill_receipt_number'}
//                                 onChange={formik.handleChange}
//                                 value={formik.values.bill_receipt_number}
//                                 placeholder='Asset Name'
//                             />
//                             {formik.touched.bill_receipt_number && formik.errors.bill_receipt_number && (
//                                 <FeildError>{formik.errors.bill_receipt_number}</FeildError>
//                             )}
//                         </FeildWraper>

//                         <FeildWraper>
//                             <FormLabel required>Vendor</FormLabel>

//                             <SelectSearch
//                                 name="vendor_id"
//                                 placeholder="- Select Vendor -"
//                                 onChange={(val) => {
//                                     formik.setFieldValue("vendor_id", val);
//                                 }}
//                                 value={formik.values.vendor_id}
//                                 options={dropdowns?.vendor?.map((el) => ({
//                                     label: el.full_name,
//                                     value: el.id,
//                                 }))}
//                             />
//                             {formik.touched.vendor_id && formik.errors.vendor_id && (
//                                 <FeildError>{formik.errors.vendor_id}</FeildError>
//                             )}
//                         </FeildWraper>

//                         <FeildWraper>
//                             <FormLabel required>Total Amount</FormLabel>
//                             <input
//                                 type={'number'}
//                                 name={'total_amount'}
//                                 onChange={formik.handleChange}
//                                 value={formik.values.total_amount}
//                                 placeholder='Bill Amount'
//                             />
//                             {formik.touched.total_amount && formik.errors.total_amount && (
//                                 <FeildError>{formik.errors.total_amount}</FeildError>
//                             )}
//                         </FeildWraper>

//                         <FeildWraper>
//                             <FormLabel required>Building</FormLabel>

//                             <SelectSearch
//                                 name="location_id"
//                                 placeholder="- Select Building -"
//                                 onChange={(val) => {
//                                     formik.setFieldValue("location_id", val);
//                                 }}
//                                 value={formik.values.location_id}
//                                 options={dropdowns?.buildings?.map((el) => ({
//                                     label: el.name,
//                                     value: el.id,
//                                 }))}
//                             />
//                             {formik.touched.location_id && formik.errors.location_id && (
//                                 <FeildError>{formik.errors.location_id}</FeildError>
//                             )}
//                         </FeildWraper>

//                         <FeildWraper>
//                             <FormLabel>Notes</FormLabel>

//                             <input
//                                 type={'text'}
//                                 name={'notes'}
//                                 onChange={formik.handleChange}
//                                 value={formik.values.notes}
//                                 placeholder='notes'
//                             />
//                             {formik.touched.notes && formik.errors.notes && (
//                                 <FeildError>{formik.errors.notes}</FeildError>
//                             )}
//                         </FeildWraper>

//                         <FeildWraper width='100%'>
//                             <FormLabel>Invoice</FormLabel>

//                             <FileUploadProgressBar
//                                 value={formik.values.invoice_path}
//                                 onChange={(file) => formik.setFieldValue("invoice_path", file)}
//                                 loader={formik.values.invoice_path}
//                             />
//                             {
//                                 (formik.touched.invoice_path && Boolean(formik.errors.invoice_path)) && (
//                                     <FeildError>{formik.touched.invoice_path && formik.errors.invoice_path}</FeildError>
//                                 )
//                             }
//                         </FeildWraper>

//                         {/* ATLES ONE FILL & ADD MORE ROW OPTION and remove in submit check total_amount and sum of all item's same other show error */}
//                         <FeildWraper width='100%'>
//                             <TableCard>
//                                 <Table>
//                                     <TableHead>
//                                         <TableRow>
//                                             <TableCell header sticky></TableCell>
//                                             <TableCell header>Item</TableCell>
//                                             <TableCell header>Variant</TableCell>
//                                             <TableCell header>Quantity</TableCell>
//                                             <TableCell header>Amount</TableCell>
//                                             <TableCell header>Note</TableCell>
//                                         </TableRow>
//                                     </TableHead>

//                                     <TableBody>
//                                         {formik.values.purchases.map((row, idx) => (
//                                             <TableRow key={idx}>
//                                                 <TableCell>
//                                                     <div className="flex gap-1">
//                                                         <button type="button" onClick={addRow}><Plus size={'1em'} /></button>

//                                                         {formik.values.purchases.length > 1 && (
//                                                             <button type="button" onClick={() => removeRow(idx)}><Minus size={'1em'} /></button>
//                                                         )}
//                                                     </div>
//                                                 </TableCell>

//                                                 <TableCell className={'!min-w-[120px]'}>
//                                                     <SelectSearch
//                                                         className={'!min-w-[180px]'}
//                                                         menuPosition='fixed'
//                                                         placeholder="- Select Item -"
//                                                         onChange={(val) => {
//                                                             formik.setFieldValue(`purchases.${idx}.item_id`, val);
//                                                         }}
//                                                         value={row.item_id}
//                                                         options={dropdowns?.items?.map((el) => ({
//                                                             label: el.name,
//                                                             value: el.id,
//                                                         }))}
//                                                     />
//                                                 </TableCell>

//                                                 <TableCell className={'!min-w-[120px]'}>
//                                                     <SelectSearch
//                                                         className={'!min-w-[180px]'}
//                                                         menuPosition='fixed'
//                                                         placeholder="- Select Variant -"
//                                                         onChange={(val) => {
//                                                             formik.setFieldValue(`purchases.${idx}.variant_id`, val);
//                                                         }}
//                                                         value={row.variant_id}
//                                                         options={dropdowns?.variants?.map((el) => ({
//                                                             label: el.name,
//                                                             value: el.id,
//                                                         }))}
//                                                     />
//                                                 </TableCell>

//                                                 <TableCell className={'!min-w-[120px]'}>
//                                                     <input
//                                                         className='!min-w-[130px]'
//                                                         type="number"
//                                                         value={row.quantity}
//                                                         onChange={(e) =>
//                                                             formik.setFieldValue(`purchases.${idx}.quantity`, e.target.value)
//                                                         }
//                                                     />
//                                                 </TableCell>

//                                                 <TableCell className={'!min-w-[120px]'}>
//                                                     <input
//                                                         className='!min-w-[130px]'
//                                                         type="number"
//                                                         value={row.amount}
//                                                         onChange={(e) =>
//                                                             formik.setFieldValue(`purchases.${idx}.amount`, e.target.value)
//                                                         }
//                                                     />
//                                                 </TableCell>

//                                                 <TableCell className={'!min-w-[120px]'}>
//                                                     <input
//                                                         className='!min-w-[190px]'
//                                                         type="text"
//                                                         value={row.note}
//                                                         onChange={(e) =>
//                                                             formik.setFieldValue(`purchases.${idx}.note`, e.target.value)
//                                                         }
//                                                     />
//                                                 </TableCell>
//                                             </TableRow>
//                                         ))}
//                                     </TableBody>
//                                 </Table>
//                             </TableCard>
//                         </FeildWraper>
//                     </FormArea>
//                 </div>

//                 <div className={Style.modal_foot}>
//                     {
//                         (formik?.values?.assignTo || (currentData?.id && currentData?.status == "Assigned" && formik?.values?.status != "Assigned")) ?
//                             <p className='text-red-500 text-[14px]'>{formik?.values?.assignTo ? "If you enter an Assign to Date, the asset remains in stock, it gets assigned and then immediately unassigned from the selected user." : "This action will unassign all users from the asset."}</p>
//                             :
//                             null
//                     }
//                     <button type='reset' className={`${Style.dcmb__cancel}`} onClick={() => onClose()}>Discard</button>
//                     <button type='submit' className={`${Style.dcmb__submit}`}>Apply</button>
//                 </div>
//             </form>
//         </>
//     )
// }

// export default AddPopup
